<div class="sticky -top-2 z-20 bg-neutral-10 -mt-2 pt-2">
  <div class="bg-white rounded p-3">
    <p class="subtitle-2 mb-2">{{ 'outlook.entrySearch.title' | translate }}</p>
    <kendo-textbox
      id="outlook-main-ui-entry-search-input"
      [value]="searchPhrase()"
      (valueChange)="handleSearchPhraseChange($event)"
      [disabled]="disabled()"
      [clearButton]="true"
      [placeholder]="'outlook.entrySearch.placeholder' | translate"
      class="w-full small-placeholder"
    >
      <ng-template kendoTextBoxPrefixTemplate>
        <span
          class="fa-icons icon-14 icon-magnifying-glass text-neutral-60 ml-2"
        ></span>
      </ng-template>
    </kendo-textbox>
  </div>
</div>

@if (isPanelOpen()) {
  <div class="flex flex-col gap-2 mt-2 mb-4">
    @if (isLoadingResults()) {
      <div class="flex flex-col gap-2 pt-6">
        @for (skeletonGroup of skeletonGroups; track skeletonGroup) {
          <div class="bg-white rounded py-2 px-1.5 flex flex-col gap-2">
            <maximizer-outlook-skeleton
              containerClasses="h-3 w-24 rounded bg-neutral-20 mx-1.5"
              fullWidth="none"
            ></maximizer-outlook-skeleton>
            <maximizer-outlook-skeleton-entry></maximizer-outlook-skeleton-entry>
            <maximizer-outlook-skeleton-entry></maximizer-outlook-skeleton-entry>
            <maximizer-outlook-skeleton-entry></maximizer-outlook-skeleton-entry>
          </div>
        }
      </div>
    } @else if (totalResultCount() > 0) {
      <p class="caption text-neutral-70 px-1 text-pretty">
        {{
          'outlook.entrySearch.resultsSummary'
            | translate
              : {
                  count: totalResultCount(),
                  individualsLabel:
                    ('individuals' | translateVertical | lowercase),
                }
        }}
      </p>

      <span
        kendoTooltip
        filter="[title]"
        [callout]="false"
        [showAfter]="500"
        kendoTooltipWidth="auto"
        [tooltipTemplate]="tooltipTemplate"
        class="flex flex-col gap-2"
      >
        @for (group of displayGroups(); track group.type) {
          <div class="bg-white rounded py-2 px-1.5">
            <div class="flex items-center gap-1 px-1.5 pb-1">
              <span
                class="fa-icons icon icon-14 pr-0.5"
                [class]="groupIcons[group.type]"
              >
              </span>
              <span class="subtitle-2">
                @if (group.type === 'individuals') {
                  {{ 'individuals' | translateVertical }}
                } @else {
                  {{ 'outlook.entries.' + group.type | translate }}
                }
              </span>
              <span class="text-body text-neutral-90">
                {{
                  'outlook.entrySearch.count'
                    | translate
                      : {
                          count: group.rows.length,
                          total: group.totalCount,
                        }
                }}
              </span>
            </div>

            @for (row of group.rows; track row.entry.id) {
              <div
                class="rounded-sm flex items-center justify-between p-1.5 gap-2 w-full hover:bg-bush-green-15 hover:cursor-pointer"
                [attr.title]="row.title"
              >
                <button
                  class="flex items-center gap-2 grow min-w-0 border-0 bg-transparent p-0 text-left cursor-pointer"
                  (click)="openEntryDetails(row.entry)"
                >
                  <div class="flex-none w-6 my-auto">
                    <div
                      class="avatar avatar-xs avatar-neutral-20 avatar-border"
                      [class.avatar-square]="
                        row.entry.abentryType === EntryType.Individual ||
                        row.entry.abentryType === EntryType.Company
                      "
                    >
                      @if (
                        row.entry.type === EntryType.AbEntry &&
                        row.entry.abentryType !== EntryType.Company
                      ) {
                        <span>{{ row.entry | outlookAvatarLetters }}</span>
                      }

                      @if (row.entry.type === EntryType.Lead) {
                        <span
                          class="fa-icons icon icon-solid icon-12 icon-user-magnifying-glass m-auto"
                        ></span>
                      } @else if (row.entry.abentryType === EntryType.Company) {
                        <span
                          class="fa-icons icon icon-regular icon-16 icon-building m-auto"
                        ></span>
                      }
                    </div>
                  </div>

                  <div class="flex flex-col min-w-0 flex-1 overflow-hidden">
                    <p
                      class="text-body truncate w-full"
                      [innerHTML]="
                        row.entry.displayName
                          | highlightSearchText: trimmedSearchPhrase()
                      "
                    ></p>

                    @if (row.entry.jobTitle) {
                      <p
                        class="caption text-neutral-70 truncate w-full"
                        [innerHTML]="
                          row.entry.jobTitle
                            | highlightSearchText: trimmedSearchPhrase()
                        "
                      ></p>
                    }

                    @if (row.companyName) {
                      <p
                        class="caption text-neutral-70 truncate w-full"
                        [innerHTML]="
                          row.companyName
                            | highlightSearchText: trimmedSearchPhrase()
                        "
                      ></p>
                    }

                    @if (row.entry.emailAddress) {
                      <p
                        class="caption text-neutral-70 truncate w-full"
                        [innerHTML]="
                          row.entry.emailAddress
                            | highlightSearchText: trimmedSearchPhrase()
                        "
                      ></p>
                    }

                    @if (row.entry.phoneNumber) {
                      <p
                        class="caption text-neutral-70 truncate w-full"
                        [innerHTML]="
                          row.entry.phoneNumber
                            | highlightSearchText: trimmedSearchPhrase()
                        "
                      ></p>
                    }

                    @if (row.entry.lastContactDate) {
                      <p class="caption text-neutral-70 truncate w-full">
                        {{ 'outlook.entryPage.lastContactedDate' | translate }}
                        {{ row.entry.lastContactDate | date: 'MMM dd, y' }}
                      </p>
                    } @else if (row.entry.lastModifiedDate) {
                      <p class="caption text-neutral-70 truncate w-full">
                        {{ 'outlook.entrySearch.lastModifiedDate' | translate }}
                        {{ row.entry.lastModifiedDate | date: 'MMM dd, y' }}
                      </p>
                    }

                    @if (row.status; as status) {
                      <span
                        class="caption rounded-xl px-2 py-0.5 mt-1 self-start max-w-full truncate"
                        [class]="status.chipClass"
                      >
                        {{ status.label | translate }}
                      </span>
                    }
                  </div>
                </button>

                <div class="flex-shrink-0">
                  @if (row.singleAction; as action) {
                    <div [attr.title]="action.tooltip">
                      <button
                        kendoButton
                        id="outlook-main-ui-entry-search-{{ action.id }}-{{
                          row.entry.id
                        }}"
                        themeColor="primary"
                        fillMode="flat"
                        [attr.aria-label]="action.text"
                        [disabled]="!!action.disabled"
                        [iconClass]="action.iconClass"
                        (click)="action.click?.()"
                      ></button>
                    </div>
                  } @else {
                    <kendo-dropdownbutton
                      [title]="'outlook.compose.actions' | translate"
                      [data]="row.actions"
                      themeColor="tertiary"
                      iconClass="fa-icons icon-ellipsis-vertical icon-16 px-1"
                      [popupSettings]="{ appendTo: viewContainerRef }"
                    >
                      <ng-template kendoDropDownButtonItemTemplate let-dataItem>
                        <div
                          kendoTooltip
                          [tooltipTemplate]="tooltipTemplate"
                          position="top"
                          [callout]="false"
                          [showOn]="
                            dataItem.disabled && dataItem.title
                              ? 'hover'
                              : 'none'
                          "
                          [attr.title]="
                            dataItem.disabled ? dataItem.title : null
                          "
                          class="flex items-center gap-1 w-full"
                        >
                          @if (dataItem.icon) {
                            <span [class]="dataItem.icon" class="w-4"></span>
                          }
                          <span>{{ dataItem.text }}</span>
                        </div>
                      </ng-template>
                    </kendo-dropdownbutton>
                  }
                </div>
              </div>
            }

            @if (group.hasMore) {
              <div class="text-center mt-0.5">
                <button
                  #showMoreButton
                  kendoButton
                  themeColor="primary"
                  fillMode="flat"
                  class="text-inline-button"
                  (click)="showMore(group.type); showMoreButton.blur()"
                >
                  {{ 'outlook.buttons.showMore' | translate }}
                </button>
              </div>
            }
          </div>
        }
      </span>
    } @else {
      <div
        class="flex flex-col items-center justify-center gap-4 p-6 min-h-[calc(100vh-8rem)]"
      >
        <img
          class="w-[6.25rem] opacity-50"
          [src]="`${cdn}/assets/images/outlook/entry-search-empty.svg`"
          alt="{{ 'outlook.forms.noDataFound' | translate }}"
        />
        <p class="text-body large text-neutral-60 text-center">
          {{ 'outlook.forms.noDataFound' | translate }}
        </p>
      </div>
    }
  </div>
}

@if (scrollToTopVisible()) {
  <div class="fixed bottom-10 right-10 z-30">
    <maximizer-circle-button
      themeColor="tertiary"
      icon="arrow-up"
      size="small"
      [title]="'outlook.buttons.scrollToTop' | translate"
      (click)="scrollToTop()"
      (keydown.enter)="scrollToTop()"
    ></maximizer-circle-button>
  </div>
}

<maximizer-outlook-notification
  entryId="entry-search"
  #notification
  [closable]="true"
>
</maximizer-outlook-notification>

<ng-template #tooltipTemplate let-anchor>
  <p class="caption text-center text-neutral-90 whitespace-nowrap">
    {{ anchor.nativeElement.getAttribute('data-title') }}
  </p>
</ng-template>
